/**
 * Resolve MIME type estável para upload (presign + PUT S3).
 *
 * Em iOS/Safari o `File.type` frequentemente vem vazio; sem Content-Type explícito
 * o browser/S3 pode gravar MIME errado. Além disso, NUNCA use `new File([file], …)`
 * só para trocar o type — no WebKit isso pode gerar um File com **0 bytes**
 * (objeto “morto” no S3 com HTTP 200).
 */
const EXTENSION_MIME: Readonly<Record<string, string>> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  zip: 'application/zip',
  svg: 'image/svg+xml',
  heic: 'image/heic',
  heif: 'image/heif',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
  m4v: 'video/x-m4v',
  ogv: 'video/ogg',
  wmv: 'video/x-ms-wmv',
  '3gp': 'video/3gpp',
};

/** Alias comuns enviados por alguns SO/browsers → MIME canônico (API/S3). */
const MIME_ALIASES: Readonly<Record<string, string>> = {
  'image/jpg': 'image/jpeg',
  'video/mov': 'video/quicktime',
  'video/avi': 'video/x-msvideo',
  'video/mkv': 'video/x-matroska',
  'video/m4v': 'video/x-m4v',
  'video/ogv': 'video/ogg',
  'video/wmv': 'video/x-ms-wmv',
  'video/3gp': 'video/3gpp',
};

function extensionOf(fileName: string): string {
  const base = fileName.split(/[/\\]/).pop() ?? fileName;
  const dot = base.lastIndexOf('.');
  if (dot < 0 || dot === base.length - 1) {
    return '';
  }
  return base.slice(dot + 1).toLowerCase();
}

export function resolveUploadContentType(file: File): string {
  const raw = (file.type || '').trim().toLowerCase();
  if (raw && raw !== 'application/octet-stream') {
    return MIME_ALIASES[raw] ?? raw;
  }

  const ext = extensionOf(file.name);
  if (ext && EXTENSION_MIME[ext]) {
    return EXTENSION_MIME[ext];
  }

  return raw || 'application/octet-stream';
}

/**
 * @deprecated Preferir {@link materializeUploadFile}.
 * Não recria o File só para ajustar MIME: no Safari/iOS `new File([file], …)`
 * pode zerar o conteúdo. Retorna o mesmo objeto (MIME é aplicado no PUT).
 */
export function ensureFileWithContentType(file: File): File {
  return file;
}

/**
 * Lê os bytes AGORA (`arrayBuffer`) e reconstrói um `File` residente na heap JS,
 * com `Content-Type` já normalizado.
 *
 * Motivo: no iOS/Safari o `File` da galeria pode ser revogado, e `new File([file])`
 * sem cópia de bytes pode gerar arquivo de 0 bytes com type “certo” — exatamente
 * o sintoma de objeto morto no S3 (`Content-Length: 0`, ETag de vazio).
 */
export async function materializeUploadFile(file: File): Promise<File> {
  const type = resolveUploadContentType(file);
  let buffer: ArrayBuffer;
  try {
    buffer = await file.arrayBuffer();
  } catch {
    throw new Error('EMPTY_FILE');
  }
  if (buffer.byteLength === 0) {
    throw new Error('EMPTY_FILE');
  }
  return new File([buffer], file.name, {
    type,
    lastModified: file.lastModified,
  });
}
